import { getId } from '@adalo/utils'

const STORAGE_KEY = 'adalo_action_clipboard'

/**
 * Builds lookup maps from target app datasources so we can remap
 * source-app IDs to target-app IDs by matching table/field names.
 *
 * Returns { tableMap, fieldMap, datasourceMap }
 *  - tableMap:      { [sourceTableId]: targetTableId }
 *  - fieldMap:      { [sourceTableId]: { [sourceFieldId]: targetFieldId } }
 *  - datasourceMap: { [sourceDsId]: targetDsId }
 */
const buildRemapMaps = (sourceDatasources, targetDatasources) => {
  const tableMap = {}
  const fieldMap = {}
  const datasourceMap = {}

  // Index target tables by name -> { datasourceId, tableId, fields }
  const targetTablesByName = {}

  for (const ds of targetDatasources) {
    if (!ds.tables) continue

    for (const [tableId, table] of Object.entries(ds.tables)) {
      if (table.name) {
        targetTablesByName[table.name.toLowerCase()] = {
          datasourceId: ds.id,
          tableId,
          fields: table.fields || {},
        }
      }
    }
  }

  // Match source tables to target tables by name
  for (const ds of sourceDatasources) {
    if (!ds.tables) continue

    for (const [srcTableId, srcTable] of Object.entries(ds.tables)) {
      if (!srcTable.name) continue

      const target = targetTablesByName[srcTable.name.toLowerCase()]

      if (!target) continue

      datasourceMap[ds.id] = target.datasourceId
      tableMap[srcTableId] = target.tableId

      // Build field mapping for this table pair
      const targetFieldsByName = {}

      for (const [fId, f] of Object.entries(target.fields)) {
        if (f.name) {
          targetFieldsByName[f.name.toLowerCase()] = fId
        }
      }

      fieldMap[srcTableId] = {}
      const srcFields = srcTable.fields || {}

      for (const [srcFieldId, srcField] of Object.entries(srcFields)) {
        if (!srcField.name) continue

        const targetFieldId = targetFieldsByName[srcField.name.toLowerCase()]

        if (targetFieldId) {
          fieldMap[srcTableId][srcFieldId] = targetFieldId
        }
      }
    }
  }

  return { tableMap, fieldMap, datasourceMap }
}

/**
 * Remaps a single fieldId using the field map.
 * fieldId can be a string ID or an object with nested source.
 */
const remapFieldId = (fieldId, sourceTableId, fieldMap) => {
  if (!fieldId || !sourceTableId) return fieldId

  const tableFieldMap = fieldMap[sourceTableId]

  if (!tableFieldMap) return fieldId

  if (typeof fieldId === 'string') {
    return tableFieldMap[fieldId] || fieldId
  }

  return fieldId
}

/**
 * Remaps datasource/table/field references in a single action item's options.
 */
const remapActionOptions = (actionItem, maps) => {
  const { tableMap, fieldMap, datasourceMap } = maps
  const opts = actionItem.options

  if (!opts) return actionItem

  const remapped = { ...actionItem, options: { ...opts } }
  const o = remapped.options

  // Remap datasourceId
  if (o.datasourceId && datasourceMap[o.datasourceId]) {
    o.datasourceId = datasourceMap[o.datasourceId]
  }

  // Derive source table ID from tableId or bindingId.tableId (UPDATE_OBJECT uses bindingId)
  const srcTableId = opts.tableId || opts.bindingId?.tableId

  if (o.tableId && tableMap[o.tableId]) {
    o.tableId = tableMap[o.tableId]
  }

  // Remap fields array (CREATE_OBJECT, UPDATE_OBJECT, SIGNUP)
  if (Array.isArray(o.fields) && srcTableId) {
    o.fields = o.fields
      .map(f => {
        const newFieldId = remapFieldId(f.fieldId, srcTableId, fieldMap)

        // If field couldn't be mapped, drop it
        if (
          typeof f.fieldId === 'string' &&
          newFieldId === f.fieldId &&
          tableMap[srcTableId]
        ) {
          return null
        }

        return { ...f, fieldId: newFieldId }
      })
      .filter(Boolean)
  }

  // Remap bindingId (UPDATE_OBJECT, DELETE_OBJECT) — clear if can't remap
  if (o.bindingId && typeof o.bindingId === 'object') {
    const remappedBindingId = { ...o.bindingId }
    let shouldClear = false

    if (remappedBindingId.datasourceId) {
      if (datasourceMap[remappedBindingId.datasourceId]) {
        remappedBindingId.datasourceId =
          datasourceMap[remappedBindingId.datasourceId]
      } else {
        shouldClear = true
      }
    }

    if (remappedBindingId.tableId) {
      if (tableMap[remappedBindingId.tableId]) {
        remappedBindingId.tableId = tableMap[remappedBindingId.tableId]
      } else {
        shouldClear = true
      }
    }

    o.bindingId = shouldClear ? undefined : remappedBindingId
  }

  // Remap association objects
  for (const key of ['object1', 'object2']) {
    if (o[key] && typeof o[key] === 'object') {
      o[key] = { ...o[key] }

      if (o[key].datasourceId && datasourceMap[o[key].datasourceId]) {
        o[key].datasourceId = datasourceMap[o[key].datasourceId]
      }

      if (o[key].tableId && tableMap[o[key].tableId]) {
        o[key].tableId = tableMap[o[key].tableId]
      }
    }
  }

  // Remap fieldId at options level (CREATE_ASSOCIATION, DELETE_ASSOCIATION)
  if (typeof o.fieldId === 'string' && srcTableId) {
    o.fieldId = remapFieldId(o.fieldId, srcTableId, fieldMap)
  }

  // Clear screen references (can't remap across apps), preserve 'back' navigation
  if (
    o.target &&
    ((typeof o.target === 'string' && o.target !== 'back') ||
      typeof o.target === 'object')
  ) {
    o.target = undefined
  }

  if (o.screenId) {
    o.screenId = undefined
  }

  return remapped
}

/**
 * Remaps all datasource/table/field references in a cloned action chain
 * so it can be pasted into a different app.
 *
 * sourceDatasources and targetDatasources should be arrays of
 * { id, tables: { [tableId]: { name, fields: { [fieldId]: { name, type } } } } }
 */
export const remapActionChain = (
  action,
  sourceDatasources,
  targetDatasources
) => {
  if (!sourceDatasources || !targetDatasources) return action

  const maps = buildRemapMaps(sourceDatasources, targetDatasources)

  if (!action.actions) return action

  return {
    ...action,
    actions: action.actions.map(item => remapActionOptions(item, maps)),
  }
}

let listeners = []

const getClipboardData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)

    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const copyActionChain = (action, appId, datasources) => {
  if (!action) return

  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      action,
      sourceAppId: appId,
      datasources: datasources || [],
    })
  )
  listeners.forEach(fn => fn())
}

export const getActionClipboard = () => {
  const data = getClipboardData()

  // Support legacy format (raw action without wrapper)
  if (data && !data.sourceAppId && data.actions) {
    return { action: data, sourceAppId: null }
  }

  return data
}

export const hasActionClipboard = () => getClipboardData() !== null

export const subscribe = listener => {
  listeners.push(listener)

  return () => {
    listeners = listeners.filter(fn => fn !== listener)
  }
}

/**
 * Returns a deep clone of the clipboard data with fresh IDs
 * for all action items so they can be pasted without conflicts.
 * Returns { action, sourceAppId }.
 */
export const pasteActionChain = () => {
  const clipboardData = getClipboardData()

  if (!clipboardData) return null

  // Support legacy format (raw action without wrapper)
  const isLegacy = !clipboardData.sourceAppId && clipboardData.actions
  const actionData = isLegacy ? clipboardData : clipboardData.action
  const sourceAppId = isLegacy ? null : clipboardData.sourceAppId
  const sourceDatasources = isLegacy ? null : clipboardData.datasources

  if (!actionData) return null

  const cloned = JSON.parse(JSON.stringify(actionData))

  if (cloned.actions) {
    cloned.actions = cloned.actions.map(item => ({
      ...item,
      id: getId(),
    }))
  }

  return { action: cloned, sourceAppId, sourceDatasources }
}
